import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export type NotificationCategory = "payment" | "membership" | "other";

/** Bildirim tipini kategoriye eşler (Notifications sayfasıyla aynı mantık). */
export const categoryOfType = (type: string): NotificationCategory => {
  const t = (type || "").toLowerCase();
  if (t.includes("payment") || t.includes("invoice") || t.includes("checkout") || t.includes("odeme")) return "payment";
  if (t.includes("membership") || t.includes("subscription") || t.includes("plan") || t.includes("premium")) return "membership";
  return "other";
};

interface UnreadState {
  total: number;
  payment: number;
  membership: number;
  other: number;
}

const EMPTY: UnreadState = { total: 0, payment: 0, membership: 0, other: 0 };

/**
 * Okunmamış bildirim sayısını (toplam + kategori bazlı) canlı olarak izler.
 * Rozetlerin uygulama genelinde tutarlı olması için tek kaynak.
 */
export const useUnreadNotifications = () => {
  const { user } = useAuth();
  const [counts, setCounts] = useState<UnreadState>(EMPTY);

  const refresh = useCallback(async () => {
    if (!user) {
      setCounts(EMPTY);
      return;
    }
    const { data, error } = await supabase
      .from("notifications")
      .select("id, type")
      .eq("user_id", user.id)
      .is("read_at", null)
      .limit(500);

    if (error || !data) return;

    const next: UnreadState = { ...EMPTY, total: data.length };
    for (const row of data) {
      next[categoryOfType((row as { type: string }).type)] += 1;
    }
    setCounts(next);
  }, [user]);

  useEffect(() => {
    if (!user) {
      setCounts(EMPTY);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = (delay = 0) => {
      if (cancelled) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (!cancelled) void refresh();
      }, delay);
    };
    void refresh();

    // Benzersiz topic: aynı hook birden fazla bileşende (veya StrictMode'da iki
    // kez) mount edildiğinde aynı kanal örneğine subscribe sonrası callback
    // eklenmesini engeller.
    const channel = supabase
      .channel(`notifications-unread-${user.id}-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => run(0),
      )
      // Ödeme/üyelik olayları bildirim üretiyor: rozetleri de anında tazele
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "premium_requests", filter: `user_id=eq.${user.id}` },
        () => run(600),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => run(600),
      )
      .subscribe();

    const onVisible = () => {
      if (document.visibilityState === "visible") run(0);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      supabase.removeChannel(channel);
    };
  }, [user, refresh]);


  return { ...counts, refresh };
};

export default useUnreadNotifications;
